import { useState } from 'react';
import { ArrowLeftIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from "../lib/supabase";

export default function References() {
  const navigate = useNavigate();
  const supabase = getSupabaseClient();
  const [submitting, setSubmitting] = useState(false);

  // Professional reference state
  const [profName, setProfName] = useState('');
  const [profEmail, setProfEmail] = useState('');
  const [profPhone, setProfPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [profRelationship, setProfRelationship] = useState('');
  const [profDuration, setProfDuration] = useState('');

  // Format phone digits as (XXX) XXX-XXXX
  const formatPhone = (digits: string): string => {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (raw: string) => {
    // Strip everything except digits
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    setProfPhone(formatPhone(digits));
    setPhoneError('');
  };

  const validatePhone = (): boolean => {
    const digits = profPhone.replace(/\D/g, '');
    if (!digits) {
      setPhoneError('Phone number is required');
      return false;
    }
    if (digits.length !== 10) {
      setPhoneError('Please enter a valid 10-digit US phone number');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validatePhone()) return;
    setSubmitting(true);
    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        alert('Not logged in');
        return;
      }

      // Insert reference directly into the database
      const { error: insertError } = await supabase.from("guide_references").upsert([
        {
          user_id: user.id,
          ref_type: "professional",
          full_name: profName,
          email: profEmail,
          phone: `+1${profPhone.replace(/\D/g, '')}`,
          company,
          job_title: jobTitle,
          professional_relationship: profRelationship,
          duration_of_relationship: profDuration,
        },
      ]);

      if (insertError) {
        console.error('Insert error:', insertError);
        alert('Failed to save references');
        return;
      }

      console.log('References saved successfully');
      navigate('/onboarding/complete-profile');
    } catch (err) {
      console.error('Unexpected error:', err);
      alert('Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fffcef] px-4 py-10">
      <div className="mx-auto max-w-3xl text-[#304031]">
        {/* Header */}
        <h1 className="mb-3 text-center font-recoleta text-[42px] leading-[44px] font-medium text-[#304031]">
          Professional References
        </h1>
        <p className="mb-12 text-center text-lg text-[#304031]/70">
          Provide a reference to help us verify your professional background and expertise.
        </p>

        {/* Professional Reference */}
        <div className="bg-[#fffcef] border-2 border-[#a0978a] rounded-2xl p-8 mb-8 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-[#304031]">Professional Reference</h2>
            <span className="text-xs font-semibold uppercase tracking-wide text-[#304031]/60 border-2 border-[#304031]/20 px-3 py-1 rounded-full">
              Required
            </span>
          </div>
          <p className="text-sm text-[#304031]/70 mb-6">
            Provide details of a professional colleague, mentor, or supervisor who can speak to your expertise and character.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-[#304031] mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={profName}
                onChange={(e) => setProfName(e.target.value)}
                placeholder="Enter reference's full name"
                className="border-2 border-[#a0978a] rounded-lg px-4 py-3 text-sm w-full bg-[#fffcef] text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#304031] mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={profEmail}
                onChange={(e) => setProfEmail(e.target.value)}
                placeholder="reference@example.com"
                className="border-2 border-[#a0978a] rounded-lg px-4 py-3 text-sm w-full bg-[#fffcef] text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-[#304031] mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <span className="inline-flex items-center rounded-lg border-2 border-[#a0978a] bg-[#304031]/5 px-3 py-3 text-sm font-medium text-[#304031]">
                  +1
                </span>
                <input
                  type="tel"
                  value={profPhone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="(555) 000-0000"
                  className={`border-2 rounded-lg px-4 py-3 text-sm flex-1 bg-[#fffcef] text-[#304031] placeholder:text-[#304031]/40 focus:outline-none focus:ring-2 focus:ring-[#304031]/20 ${
                    phoneError ? 'border-red-400 focus:border-red-500' : 'border-[#a0978a] focus:border-[#304031]'
                  }`}
                />
              </div>
              {phoneError && (
                <p className="mt-1 text-xs text-red-500">{phoneError}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#304031] mb-2">Job Title</label>
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Professional title"
                className="border-2 border-[#a0978a] rounded-lg px-4 py-3 text-sm w-full bg-[#fffcef] text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-[#304031] mb-2">
              Company/Organization <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Company or organization name"
              className="border-2 border-[#a0978a] rounded-lg px-4 py-3 text-sm w-full bg-[#fffcef] text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-[#304031] mb-2">
              Professional Relationship
            </label>
            <textarea
              rows={3}
              value={profRelationship}
              onChange={(e) => setProfRelationship(e.target.value)}
              placeholder="Describe your professional relationship (e.g., former supervisor, colleague, mentor)"
              className="border-2 border-[#a0978a] rounded-lg px-4 py-3 text-sm w-full bg-[#fffcef] text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#304031] mb-2">
              Duration of Relationship
            </label>
            <input
              type="text"
              value={profDuration}
              onChange={(e) => setProfDuration(e.target.value)}
              placeholder="e.g., 3 years (2020-2023)"
              className="border-2 border-[#a0978a] rounded-lg px-4 py-3 text-sm w-full bg-[#fffcef] text-[#304031] placeholder:text-[#304031]/40 focus:border-[#304031] focus:outline-none focus:ring-2 focus:ring-[#304031]/20"
            />
          </div>
        </div>

        {/* Verification Process Info */}
        <div className="bg-[#304031]/5 border-2 border-[#a0978a] rounded-2xl p-6 text-sm text-[#304031] flex gap-4 mb-8">
          <InformationCircleIcon className="h-6 w-6 text-[#304031] mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-[#304031] mb-3">Reference Verification Process:</p>
            <ul className="space-y-2 text-[#304031]/70">
              <li className="flex items-start gap-2">
                <span className="text-[#304031] font-bold">•</span>
                <span>References will be contacted within 2–3 business days</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#304031] font-bold">•</span>
                <span>A brief verification call or email will be conducted</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#304031] font-bold">•</span>
                <span>References should expect 5–10 minutes of their time</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#304031] font-bold">•</span>
                <span>All reference information is kept confidential</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-4 items-center justify-between">
          <button
            onClick={() => navigate('/onboarding/complete-profile')}
            disabled={submitting}
            className="inline-flex items-center gap-2 border-2 border-[#304031] px-6 py-3 rounded-full hover:bg-[#304031]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-[#304031]"
          >
            ← Back
          </button>
          <button
            onClick={handleSave}
            disabled={submitting}
            className={`inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold transition ${
              submitting
                ? 'bg-[#304031]/20 text-[#304031]/40 cursor-not-allowed'
                : 'bg-[#304031] text-[#fef6d6] hover:bg-[#304031]/90'
            }`}
          >
            {submitting ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-[#fef6d6] border-t-transparent rounded-full"></div>
                Submitting...
              </>
            ) : (
              'Save & Continue →'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
